import { useState, useEffect, useCallback, useMemo } from 'react'
import { todolistAPI } from '../../services/api'
import { getAvatarData } from '../../utils/avatarUtils'
import CallToggle from '../calls/CallToggle'
import NotesSection from '../todolist/NotesSection'

// ========================================
// Utilidades de fecha (timezone-safe, usa strings YYYY-MM-DD)
// ========================================

// Extraer YYYY-MM-DD de cualquier formato de fecha (evita timezone issues)
const toDateKey = (dateStr) => {
  if (!dateStr) return ''
  const s = String(dateStr)
  // Si viene como ISO "2026-01-26T00:00:00.000Z" tomar solo la parte de fecha
  return s.includes('T') ? s.split('T')[0] : s.split(' ')[0]
}

// YYYY-MM-DD de hoy en hora local
const todayKey = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// Diferencia en días entre dos date keys YYYY-MM-DD
const diffDays = (keyA, keyB) => {
  const a = new Date(keyA + 'T12:00:00') // mediodía para evitar DST issues
  const b = new Date(keyB + 'T12:00:00')
  return Math.round((a - b) / (1000 * 60 * 60 * 24))
}

const formatShortDate = (dateStr) => {
  const key = toDateKey(dateStr)
  const [y, m, d] = key.split('-').map(Number)
  const date = new Date(y, m - 1, d) // constructor local, sin timezone
  return date.toLocaleDateString('es-CL', { day: 'numeric', month: 'short' })
}

const formatWeekday = (dateStr) => {
  const key = toDateKey(dateStr)
  const [y, m, d] = key.split('-').map(Number)
  const date = new Date(y, m - 1, d)
  return date.toLocaleDateString('es-CL', { weekday: 'long' })
}

const getDayLabel = (dateKey) => {
  const today = todayKey()
  const diff = diffDays(dateKey, today)

  if (diff < 0) return { label: `Vencido (${Math.abs(diff)}d)`, level: 'overdue' }
  if (diff === 0) return { label: 'Hoy', level: 'today' }
  if (diff === 1) return { label: 'Mañana', level: 'tomorrow' }
  return { label: `${formatWeekday(dateKey)} ${formatShortDate(dateKey)}`, level: 'future' }
}

// ========================================
// Estilos por nivel de urgencia
// ========================================

const GROUP_STYLES = {
  overdue: {
    header: 'bg-red-50 text-red-800 border-red-200',
    icon: '🔴',
    dot: 'bg-red-500 animate-pulse'
  },
  today: {
    header: 'bg-orange-50 text-orange-800 border-orange-200',
    icon: '🟠',
    dot: 'bg-orange-500 animate-pulse'
  },
  tomorrow: {
    header: 'bg-amber-50 text-amber-700 border-amber-200',
    icon: '🟡',
    dot: 'bg-amber-400'
  },
  future: {
    header: 'bg-gray-50 text-gray-700 border-gray-200',
    icon: '📅',
    dot: 'bg-gray-400'
  }
}

// ========================================
// Componente: Tarjeta de contacto
// ========================================

function ContactCard({ contact, isExpanded, onToggle, onNotesUpdate }) {
  const avatarName = contact.name || contact.phone
  const avatar = getAvatarData(avatarName, contact.phone)
  const displayName = contact.name || 'Sin nombre'

  return (
    <div className="mx-3 mb-2 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
      {/* Fila principal compacta - solo esta zona colapsa/expande */}
      <div className="px-3 py-2.5 flex items-center gap-3 cursor-pointer" onClick={onToggle}>
        {/* Avatar con degradado determinístico (coincide con TodoList/Chats/ProspectPanel) */}
        <div
          className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-bold shadow-sm"
          style={{ background: avatar.gradient }}
        >
          {avatar.initials}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            {contact.icon_current && <span className="text-sm">{contact.icon_current}</span>}
            <span className="text-sm font-medium text-gray-900 truncate">
              {displayName}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            {contact.business_type && (
              <span className="text-xs text-gray-500 truncate">{contact.business_type}</span>
            )}
            {!contact.business_type && contact.phone && (
              <span className="text-xs text-gray-400">{contact.phone}</span>
            )}
          </div>
        </div>

        {/* Flecha expandir */}
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {/* Última nota (preview cuando está colapsado) */}
      {contact.last_note && !isExpanded && (
        <div className="px-3 pb-2.5 -mt-1 cursor-pointer" onClick={onToggle}>
          <p className="text-xs text-gray-500 truncate pl-12">
            <span className="text-gray-400">📝</span> {contact.last_note}
          </p>
        </div>
      )}

      {/* Panel expandido */}
      {isExpanded && (
        <div className="border-t border-gray-50 mt-1">
          {/* Detalles básicos */}
          <div className="px-3 pt-2 pb-1 space-y-2">
            {/* Teléfono + CallToggle */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs">
                <span className="text-gray-400 w-4 text-center">📱</span>
                <span className="text-gray-600 font-mono">{contact.phone}</span>
              </div>
              <CallToggle contact={{ id: contact.id, phone: contact.phone, name: displayName }} />
            </div>

            {/* Negocio */}
            {contact.business_type && (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-gray-400 w-4 text-center">🏪</span>
                <span className="text-gray-600">{contact.business_type}</span>
              </div>
            )}

            {/* Fecha creación */}
            <div className="flex items-center gap-2 text-xs">
              <span className="text-gray-400 w-4 text-center">📋</span>
              <span className="text-gray-500">Creado: {formatShortDate(contact.created_at)}</span>
            </div>

            {/* Fecha vencimiento */}
            <div className="flex items-center gap-2 text-xs">
              <span className="text-gray-400 w-4 text-center">📅</span>
              <span className="text-gray-500">Vence: {formatShortDate(contact.next_followup_date)}</span>
            </div>

            {/* Asignado */}
            {contact.assigned_to && (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-gray-400 w-4 text-center">👤</span>
                <span className="text-gray-500">{contact.assigned_to}</span>
              </div>
            )}

            {/* Stage si existe */}
            {contact.stage && contact.stage !== 'bombilla_apagada' && (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-gray-400 w-4 text-center">🎯</span>
                <span className="text-gray-500 capitalize">{contact.stage.replace(/_/g, ' ')}</span>
              </div>
            )}
          </div>

          {/* Notas Inteligentes (sistema completo con CRUD + acciones) */}
          <div className="mt-1">
            <NotesSection phone={contact.phone} onProspectUpdate={onNotesUpdate} />
          </div>
        </div>
      )}
    </div>
  )
}

// ========================================
// Componente: Grupo de día
// ========================================

function DayGroup({ dateKey, contacts, level, expandedId, onToggleCard, onNotesUpdate }) {
  const style = GROUP_STYLES[level] || GROUP_STYLES.future
  const dayInfo = getDayLabel(dateKey)

  return (
    <div className="mb-1">
      {/* Header del grupo - sticky dentro del scroll */}
      <div className={`sticky top-0 z-10 px-4 py-2 flex items-center gap-2 text-xs font-semibold border-b ${style.header}`}>
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${style.dot}`} />
        <span className="uppercase tracking-wide">{dayInfo.label}</span>
        <span className="text-gray-400 font-normal">({contacts.length})</span>
      </div>

      {/* Tarjetas */}
      <div className="py-2">
        {contacts.map(contact => (
          <ContactCard
            key={contact.id}
            contact={contact}
            isExpanded={expandedId === contact.id}
            onToggle={() => onToggleCard(contact.id)}
            onNotesUpdate={onNotesUpdate}
          />
        ))}
      </div>
    </div>
  )
}

// ========================================
// Componente principal: UpcomingPanel
// ========================================

export default function UpcomingPanel({ isOpen, onClose }) {
  const [contacts, setContacts] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [expandedId, setExpandedId] = useState(null)

  // Fetch contacts
  const fetchUpcoming = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await todolistAPI.getUpcoming(14)
      setContacts(result.contacts || [])
    } catch (err) {
      console.error('Error fetching upcoming:', err)
      setError('Error cargando agenda')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (isOpen) {
      fetchUpcoming()
    }
  }, [isOpen, fetchUpcoming])

  // Agrupar contactos por día (timezone-safe)
  const groups = useMemo(() => {
    const map = new Map()
    const today = todayKey()

    for (const contact of contacts) {
      const key = toDateKey(contact.next_followup_date)
      if (!key) continue
      if (!map.has(key)) {
        const diff = diffDays(key, today)
        const level = diff < 0 ? 'overdue' : diff === 0 ? 'today' : diff === 1 ? 'tomorrow' : 'future'
        map.set(key, { contacts: [], level })
      }
      map.get(key).contacts.push(contact)
    }

    return Array.from(map.entries()).map(([key, val]) => ({
      dateKey: key,
      level: val.level,
      contacts: val.contacts
    }))
  }, [contacts])

  // Contadores para el resumen
  const counts = useMemo(() => {
    const c = { overdue: 0, today: 0, tomorrow: 0, future: 0 }
    for (const g of groups) {
      c[g.level] += g.contacts.length
    }
    return c
  }, [groups])

  const toggleCard = (id) => {
    setExpandedId(prev => prev === id ? null : id)
  }

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/10 backdrop-blur-[1px] transition-opacity duration-300"
          onClick={onClose}
        />
      )}

      {/* Panel */}
      <div
        className={`fixed top-16 right-0 bottom-0 z-30 w-[380px] bg-white shadow-2xl border-l border-gray-200 flex flex-col transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header del panel */}
        <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-white to-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-lg">🗓️</span>
              <h2 className="text-sm font-semibold text-gray-900">Agenda de Seguimiento</h2>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Resumen de contadores */}
          {!loading && contacts.length > 0 && (
            <div className="flex items-center gap-3 mt-2 text-xs">
              {counts.overdue > 0 && (
                <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">
                  {counts.overdue} vencido{counts.overdue > 1 ? 's' : ''}
                </span>
              )}
              {counts.today > 0 && (
                <span className="px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">
                  {counts.today} hoy
                </span>
              )}
              {counts.tomorrow > 0 && (
                <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                  {counts.tomorrow} mañana
                </span>
              )}
              {counts.future > 0 && (
                <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium">
                  {counts.future} próximos
                </span>
              )}
            </div>
          )}
        </div>

        {/* Contenido scrolleable */}
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-16">
              <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              <span className="ml-3 text-sm text-gray-500">Cargando agenda...</span>
            </div>
          )}

          {error && (
            <div className="m-4 p-3 bg-red-50 rounded-lg text-sm text-red-600">
              {error}
              <button onClick={fetchUpcoming} className="ml-2 underline hover:no-underline">Reintentar</button>
            </div>
          )}

          {!loading && !error && contacts.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <span className="text-4xl mb-3">✨</span>
              <p className="text-sm font-medium">Sin vencimientos próximos</p>
              <p className="text-xs mt-1">No hay contactos con fecha de seguimiento</p>
            </div>
          )}

          {!loading && !error && groups.map(group => (
            <DayGroup
              key={group.dateKey}
              dateKey={group.dateKey}
              contacts={group.contacts}
              level={group.level}
              expandedId={expandedId}
              onToggleCard={toggleCard}
              onNotesUpdate={fetchUpcoming}
            />
          ))}
        </div>

        {/* Footer con total */}
        {!loading && contacts.length > 0 && (
          <div className="flex-shrink-0 px-4 py-2 border-t border-gray-100 bg-gray-50 text-xs text-gray-500 text-center">
            {contacts.length} contacto{contacts.length > 1 ? 's' : ''} en agenda
          </div>
        )}
      </div>
    </>
  )
}
